import React, { createContext, useContext, useState, useCallback } from 'react';
import { ToastType } from '../components/ui/Toast';
import ToastContainer from '../components/ui/ToastContainer';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  persistent?: boolean;
  logoSrc?: string;
}

interface ToastContextType {
  showToast: (type: ToastType, title: string, message?: string, duration?: number, persistent?: boolean) => void;
  showSuccess: (title: string, message?: string, persistent?: boolean) => void;
  showError: (title: string, message?: string, persistent?: boolean) => void;
  showWarning: (title: string, message?: string, persistent?: boolean) => void;
  showInfo: (title: string, message?: string, persistent?: boolean) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: React.ReactNode;
  logoSrc?: string; // Configurable logo source for all toasts
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  logoSrc = '/logo.png' // Default placeholder logo
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const generateId = () => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const showToast = useCallback((
    type: ToastType,
    title: string,
    message?: string,
    duration: number = type === 'error' ? 6000 : 4000,
    persistent: boolean = false
  ) => {
    const id = generateId();
    const newToast: Toast = {
      id,
      type,
      title,
      message,
      duration: persistent ? 0 : duration,
      persistent,
      logoSrc // Use the configurable logo
    };

    setToasts(prev => [...prev, newToast]);
  }, [logoSrc]);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showSuccess = useCallback((title: string, message?: string, persistent: boolean = false) => {
    showToast('success', title, message, 4000, persistent);
  }, [showToast]);

  const showError = useCallback((title: string, message?: string, persistent: boolean = false) => {
    showToast('error', title, message, 6000, persistent);
  }, [showToast]);

  const showWarning = useCallback((title: string, message?: string, persistent: boolean = false) => {
    showToast('warning', title, message, 5000, persistent);
  }, [showToast]);

  const showInfo = useCallback((title: string, message?: string, persistent: boolean = false) => {
    showToast('info', title, message, 4000, persistent);
  }, [showToast]);

  const contextValue: ToastContextType = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
};

export default ToastContext;
